/**
 * AI consent (Apple 5.1.2(i), GDPR Art. 6/7/13): a receipt is sent to the AI provider only when the
 * person who uploads it agreed to the current consent text. Stored per user (docs/06-beleg-ki.md).
 * Bump the version when the consent text changes materially; users who agreed earlier are asked again.
 */
export const AI_CONSENT_VERSION = 1;

/** `granted: false` records an explicit "no" (declined or revoked), so the dialog is not shown again. */
export type AiConsent = { granted: boolean; at: string; version: number };

type ConsentLike = { granted: boolean; version: number } | null | undefined;

/** True when receipts of this person may be sent to the AI provider. */
export const hasAiConsent = (c: ConsentLike) => !!c?.granted && c.version >= AI_CONSENT_VERSION;

/** True when the consent dialog must be shown before an upload (never decided, or agreed to an older text). */
export const needsAiConsentDecision = (c: ConsentLike) => !c || (c.granted && c.version < AI_CONSENT_VERSION);

/**
 * Current versions of the legal texts a landlord accepts when creating an account (AGB = contract, Datenschutz = Art. 13
 * information, acknowledged). The version is the "Stand" date of the page on the landing (apps/landing/src/config/legal.ts
 * reads it from here). Change the date together with the text; users who accepted an older version are asked again
 * (non-blocking banner), new accounts need the current one.
 */
export const LEGAL_VERSIONS = { terms: '2026-10-21', privacy: '2026-10-16' } as const;

/** Stored on the user (no IP address, data minimisation). */
export type LegalConsent = { terms: string; privacy: string; at: string };

/** True when the person accepted the current AGB and acknowledged the current Datenschutzerklärung. */
export const hasLegalConsent = (c: { terms: string; privacy: string } | null | undefined) => !!c && c.terms === LEGAL_VERSIONS.terms && c.privacy === LEGAL_VERSIONS.privacy;
